import { motion } from "framer-motion";
import { MapPin, Phone } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const AREAS = [
  "Bradford",
  "Leeds",
  "Halifax",
  "Huddersfield",
  "Wakefield",
  "Keighley",
  "Shipley",
  "Bingley",
  "Ilkley",
  "Skipton",
  "Dewsbury",
  "Batley",
  "Pudsey",
  "Harrogate",
  "York",
  "Manchester",
];

const ease = [0.16, 1, 0.3, 1];

// OpenStreetMap embed centred on Bradford (53.796, -1.759), framed to show the 50-mile coverage region
const MAP_SRC =
  "https://www.openstreetmap.org/export/embed.html?bbox=-2.96%2C53.07%2C-0.56%2C54.52&layer=mapnik&marker=53.796%2C-1.759";

const CoverageMap = () => (
  <motion.div
    initial={{ opacity: 0, x: 32 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, ease }}
    className="relative"
  >
    <div className="relative aspect-[4/3] overflow-hidden rounded-3xl border border-slate-100 shadow-xl">
      <iframe
        data-testid="coverage-map-iframe"
        src={MAP_SRC}
        title="Hyek Cleaning coverage map — 50-mile radius of Bradford, West Yorkshire"
        loading="lazy"
        className="absolute inset-0 h-full w-full border-0"
      />
      <div className="pointer-events-none absolute left-4 top-4 flex items-center gap-2 rounded-full bg-white/90 px-4 py-2 shadow-sm backdrop-blur-sm">
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-teal-500 opacity-60" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-teal-600" />
        </span>
        <span className="text-xs font-bold uppercase tracking-[0.12em] text-slate-700">
          Based in Bradford
        </span>
      </div>
    </div>

    <div className="absolute -bottom-5 left-6 rounded-2xl bg-slate-900 px-5 py-3 shadow-lg">
      <p className="text-xs font-bold uppercase tracking-[0.15em] text-teal-300">Coverage</p>
      <p className="text-sm font-semibold text-white">50-mile radius of Bradford, West Yorkshire</p>
    </div>
  </motion.div>
);

export const ServiceArea = () => (
  <section id="areas" data-testid="service-area-section" className="bg-white py-24 lg:py-32">
    <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-16 px-6 lg:grid-cols-2 lg:gap-20 lg:px-12">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease }}
      >
        <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-teal-700">Service Area</p>
        <h2 className="font-serif text-4xl font-medium tracking-tight text-slate-900 sm:text-5xl">
          Proudly cleaning across <span className="italic text-teal-700">West Yorkshire.</span>
        </h2>
        <p className="mt-5 max-w-md text-sm leading-relaxed text-slate-500 sm:text-base">
          We're a local Bradford team through and through — which means fast response times,
          flexible slots and a cleaner who's never far away. We cover homes and businesses within
          a 50-mile radius, including:
        </p>
        <ul className="mt-8 flex flex-wrap gap-3" data-testid="area-chips">
          {AREAS.map((a, i) => (
            <motion.li
              key={a}
              data-testid={`area-chip-${i}`}
              initial={{ opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.04, ease }}
              className={`flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-bold ${
                a === "Bradford"
                  ? "border-teal-700 bg-teal-700 text-white"
                  : "border-teal-100 bg-teal-50 text-teal-800"
              }`}
            >
              <MapPin className={`h-3.5 w-3.5 ${a === "Bradford" ? "text-teal-200" : "text-teal-600"}`} />
              {a}
            </motion.li>
          ))}
        </ul>
        <p className="mt-8 max-w-md text-sm leading-relaxed text-slate-500">
          Don't see your area? We regularly travel further for end of tenancy and larger jobs —
          just ask.
        </p>
        <div className="mt-8 flex flex-wrap gap-4">
          <a
            data-testid="area-call-button"
            href="tel:07776714973"
            className="flex items-center gap-2 rounded-full bg-teal-700 px-7 py-3.5 text-sm font-bold text-white shadow-sm transition-transform duration-300 hover:scale-105 hover:bg-teal-600"
          >
            <Phone className="h-4 w-4" /> Check Your Address
          </a>
          <button
            data-testid="area-quote-button"
            onClick={() => scrollToSection("#contact")}
            className="rounded-full border border-slate-200 bg-white px-7 py-3.5 text-sm font-bold text-slate-700 transition-colors duration-300 hover:border-teal-300 hover:text-teal-700"
          >
            Get a Free Quote
          </button>
        </div>
      </motion.div>

      <CoverageMap />
    </div>
  </section>
);
